// Daily 6 AM digest admin card — configure recipients, preview + send-now.
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sunrise, Send, Eye, Loader2, Trash2, Plus, Mail } from "lucide-react";

export default function DailyDigestCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["digest-settings"],
    queryFn: async () => (await api.get("/digest/settings")).data,
  });
  const [form, setForm] = useState(null);
  const [newRecipient, setNewRecipient] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (data && !form) setForm(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => (await api.put("/digest/settings", form)).data,
    onSuccess: () => {
      toast.success("Digest settings saved");
      qc.invalidateQueries({ queryKey: ["digest-settings"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });

  const sendNow = useMutation({
    mutationFn: async () => (await api.post("/digest/send-now")).data,
    onSuccess: (r) => {
      toast.success(`Digest sent to ${r.sent} recipient(s)`);
      qc.invalidateQueries({ queryKey: ["digest-settings"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  const openPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const { data: prev } = await api.get("/digest/preview");
      setPreviewHtml(prev.html);
    } catch (e) {
      toast.error("Could not load preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (isLoading || !form) return (
    <Card className="rounded-sm border-slate-200 industrial-shadow p-5 flex items-center gap-2 text-sm text-slate-500" data-testid="digest-loading">
      <Loader2 size={14} className="animate-spin" /> Loading digest settings…
    </Card>
  );

  const addRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email"); return; }
    if (form.recipients.includes(email)) { toast.info("Already in list"); return; }
    setForm({ ...form, recipients: [...form.recipients, email] });
    setNewRecipient("");
  };

  return (
    <>
      <Card className="rounded-sm border-slate-200 industrial-shadow" data-testid="daily-digest-card">
        <div className="px-5 py-4 border-b border-slate-200 flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="stamp-label text-slate-500">Automation · Resend</div>
            <h3 className="font-display text-lg font-semibold mt-0.5 flex items-center gap-2">
              <Sunrise size={16} className="text-amber-500" /> Daily 6 AM Digest
            </h3>
            <p className="text-xs text-slate-500 mt-1">Auto-emails a morning summary of today's bookings, quotes awaiting approval, designs awaiting approval and overdue invoices.</p>
          </div>
          <Badge variant="outline" className={`rounded-sm uppercase tracking-wider text-[10px] ${form.enabled ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-100 text-slate-500"}`} data-testid="digest-enabled-badge">
            {form.enabled ? "Enabled" : "Paused"}
          </Badge>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="digest-enabled-switch" />
              <Label className="text-xs cursor-pointer" onClick={() => setForm({ ...form, enabled: !form.enabled })}>Send automatically each morning</Label>
            </div>
            <div>
              <Label className="stamp-label text-slate-600">Send at (Sydney time)</Label>
              <Input type="number" min="0" max="23" value={form.hour_local}
                onChange={(e) => setForm({ ...form, hour_local: Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)) })}
                className="rounded-sm h-9 mt-1 font-mono" data-testid="digest-hour-input" />
              <div className="text-[10px] text-slate-400 mt-0.5">{String(form.hour_local).padStart(2, "0")}:00 AEST/AEDT</div>
            </div>
          </div>

          <div>
            <Label className="stamp-label text-slate-600">Recipients</Label>
            {form.recipients.length === 0 ? (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2.5 mt-1" data-testid="digest-no-recipients">
                <Mail size={12} className="inline mr-1" />
                No recipients set — the digest will be sent to all admin users by default.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 mt-1" data-testid="digest-recipients-list">
                {form.recipients.map((email, i) => (
                  <div key={email} className="flex items-center justify-between px-3 py-2 text-xs">
                    <span className="font-mono text-slate-800">{email}</span>
                    <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, recipients: form.recipients.filter((_, j) => j !== i) })}
                      className="rounded-sm h-6 w-6 p-0 text-slate-400 hover:text-rose-600" data-testid={`digest-remove-${i}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <Input type="email" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addRecipient()}
                placeholder="add@ccfencing.industries" className="rounded-sm h-9 font-mono text-xs" data-testid="digest-add-email" />
              <Button onClick={addRecipient} className="rounded-sm h-9 bg-slate-800 hover:bg-slate-900 text-white shrink-0" data-testid="digest-add-btn">
                <Plus size={13} className="mr-1" /> Add
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-3 border-t border-slate-100">
            {[
              ["include_bookings", "Today's bookings"],
              ["include_quotes", "Pending quotes"],
              ["include_designs", "Pending designs"],
              ["include_overdue", "Overdue invoices"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
                <Switch checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`digest-toggle-${k}`} />
                {label}
              </label>
            ))}
          </div>

          {form.last_sent_at && (
            <div className="text-[10px] text-slate-500 border-t border-slate-100 pt-3" data-testid="digest-last-sent">
              Last sent: {new Date(form.last_sent_at).toLocaleString("en-AU")} · {form.last_sent_count || 0} recipient(s)
            </div>
          )}

          <div className="flex gap-2 pt-2 flex-wrap">
            <Button onClick={openPreview} variant="outline" className="rounded-sm" data-testid="digest-preview-btn">
              <Eye size={13} className="mr-1.5" /> Preview
            </Button>
            <Button onClick={() => sendNow.mutate()} disabled={sendNow.isPending}
              variant="outline" className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="digest-send-now-btn">
              {sendNow.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Send size={13} className="mr-1.5" />} Send now
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white ml-auto" data-testid="digest-save-btn">
              {save.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null} Save settings
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] flex flex-col" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <Sunrise size={18} className="text-amber-500" /> Morning Digest — Live preview
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              This is what your recipients will get at {String(form.hour_local).padStart(2, "0")}:00 Sydney time each morning.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden rounded-sm border border-slate-200 bg-white">
            {previewLoading ? (
              <div className="p-8 text-center text-sm text-slate-500"><Loader2 size={16} className="inline animate-spin mr-2" /> Building preview…</div>
            ) : (
              <iframe srcDoc={previewHtml} sandbox="" className="w-full h-[65vh]" title="Digest preview" data-testid="digest-preview-iframe" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
